import { alpha, styled } from '@mui/material/styles';
import { Box, BoxProps } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const MIN_THUMB_SIZE = 24;
const HIDE_DELAY_MS = 1200;

const RootStyle = styled('div')(({ theme }) => ({
  flexGrow: 1,
  height: '100%',
  overflow: 'hidden',
  position: 'relative'
}));

const ScrollbarStyle = styled(Box)(({ theme }) => ({
  maxHeight: '100%',
  overflow: 'auto',
  width: '100%',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    width: 0,
    height: 0
  }
}));

const Thumb = styled('div')(({ theme }) => ({
  position: 'absolute',
  right: 2,
  width: 8,
  borderRadius: 8,
  backgroundColor: alpha(theme.palette.grey[600], 0.6),
  opacity: 0,
  transition: 'opacity 0.2s ease',
  pointerEvents: 'none'
}));

export default function Scrollbar({ children, sx, ...other }: BoxProps) {
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
  const scrollRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [thumbStyle, setThumbStyle] = useState({ height: 0, top: 0, visible: false });

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      globalThis.clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const scheduleHide = useCallback(() => {
    clearHideTimeout();
    hideTimeoutRef.current = globalThis.setTimeout(() => {
      setThumbStyle((prev) => ({ ...prev, visible: false }));
    }, HIDE_DELAY_MS);
  }, [clearHideTimeout]);

  const updateThumb = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;

    const { scrollHeight, clientHeight, scrollTop } = element;
    if (scrollHeight <= clientHeight) {
      setThumbStyle({ height: 0, top: 0, visible: false });
      return;
    }

    const ratio = clientHeight / scrollHeight;
    const height = Math.max(Math.round(clientHeight * ratio), MIN_THUMB_SIZE);
    const maxTop = clientHeight - height;
    const top = Math.round((scrollTop / (scrollHeight - clientHeight)) * maxTop);

    setThumbStyle((prev) => ({
      height,
      top: Number.isFinite(top) ? top : 0,
      visible: prev.visible
    }));
  }, []);

  const showThumb = useCallback(() => {
    setThumbStyle((prev) => ({ ...prev, visible: true }));
    scheduleHide();
  }, [scheduleHide]);

  useEffect(() => {
    updateThumb();
  }, [updateThumb, children]);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    const handleScroll = () => {
      updateThumb();
      showThumb();
    };

    const handleResize = () => updateThumb();

    element.addEventListener('scroll', handleScroll, { passive: true });
    globalThis.addEventListener('resize', handleResize);

    return () => {
      element.removeEventListener('scroll', handleScroll);
      globalThis.removeEventListener('resize', handleResize);
    };
  }, [showThumb, updateThumb]);

  const isThumbVisible = useMemo(
    () => (isHovering ? true : thumbStyle.visible),
    [isHovering, thumbStyle.visible]
  );

  if (isMobile) {
    return (
      <Box sx={{ overflowX: 'auto', ...sx }} {...other}>
        {children}
      </Box>
    );
  }

  return (
    <RootStyle onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)}>
      <ScrollbarStyle ref={scrollRef} className="scrollbar-hover" sx={sx} {...other}>
        {children}
      </ScrollbarStyle>
      {thumbStyle.height > 0 && (
        <Thumb
          style={{
            height: thumbStyle.height,
            top: thumbStyle.top,
            opacity: isThumbVisible ? 1 : 0
          }}
        />
      )}
    </RootStyle>
  );
}
